import React, { useEffect, useMemo, useState } from 'react';
import { Box, Modal, Typography, Fade, Stack, Avatar } from '@mui/material';
import { styled } from '@mui/system';
import { isMobile } from 'react-device-detect';
import ReactCountryFlag from 'react-country-flag';
import { t } from '../../../i18n';
import winnerBackground from '/images/table/winner-background.png';
import winnerGraphic from '/images/table/winner-graphic.svg';
import winnerGraphicAlt from '/images/table/winner-graphic-alt.svg';

type RankingEntry = {
    id: string;
    username: string;
    balance: number;
    position: number;
    prize: number;
    country?: string;
};

interface WinTournamentModalProps {
    open: boolean;
    onClose: () => void;
    winnerInformation?: {
        id: string;
        username: string;
        country?: string;
        chips?: number;
        prize?: number;
    };
    rankingData?: RankingEntry[];
    secondsToClose?: number;
    closeLastTableUponWinSeconds?: number;
    tournamentId?: string;
    formatCurrency: (amount: number) => string;
    container?: HTMLElement | null;
    onShowNotification?: (message: string, variant?: 'success' | 'error' | 'info' | 'warning') => void;
    currentUsername?: string;
}

const StyledModal = styled(Modal)({
    display: 'flex',
    background: 'none',
    pointerEvents: 'none',
    alignItems: 'center',
    justifyContent: 'center'
});

const ContentBox = styled(Box)({
    backgroundColor: '#1d1d28',
    borderRadius: '6px',
    minWidth: isMobile ? '90%' : '560px',
    maxWidth: isMobile ? '90%' : 'fit-content',
    color: 'white',
    textAlign: 'left',
    position: 'relative',
    maxHeight: '90vh',
    boxSizing: 'border-box',
    pointerEvents: 'auto'
});

const HeaderRow = styled(Box)({
    backgroundColor: '#24242f',
    backgroundImage: `url(${winnerBackground})`,
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: isMobile ? '0.75rem' : '0.75rem 1.5rem',
    boxSizing: 'border-box',
    borderRadius: '6px'
});

const WinnerTitle = styled(Box)({
    color: '#cc976d',
    fontSize: isMobile ? '20px' : '24px',
    fontWeight: 600,
    fontFamily: '"Inter", serif'
});

const WinnerName = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#ffffff',
    fontSize: '14px',
    lineHeight: 1,
    fontWeight: '600',
    textAlign: 'center',
    fontFamily: '"Inter", serif'
});

const RankingsTable = styled(Box)({
    width: '100%',
    marginTop: '16px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
    maxHeight: '280px',
    minHeight: '120px',
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingRight: '4px',
    /* Webkit scrollbar */
    '&::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
    },
    '&::-webkit-scrollbar-track': {
        background: 'rgba(255,255,255,0.08)',
        borderRadius: '4px',
        margin: '2px',
    },
    '&::-webkit-scrollbar-thumb': {
        background: 'rgba(255,255,255,0.4)',
        borderRadius: '4px',
        border: '1px solid rgba(255,255,255,0.15)',
        '&:hover': {
            background: 'rgba(255,255,255,0.6)',
        },
        '&:active': {
            background: 'rgba(255,255,255,0.7)',
        },
    },
    '&::-webkit-scrollbar-corner': {
        background: 'transparent',
    },
    /* Firefox scrollbar */
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(255,255,255,0.4) rgba(255,255,255,0.08)',
});

const RankingsRow = styled(Box)({
    display: 'grid',
    gridTemplateColumns: isMobile ? 'minmax(0, 32px) minmax(0, 1fr) minmax(0, 100px)' : '48px 1fr 110px 110px',
    gap: '8px',
    alignItems: 'center',
    padding: '8px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    fontFamily: '"Inter", serif',
    minWidth: 0,
    width: '100%',
    boxSizing: 'border-box'
});

const WinTournamentModal: React.FC<WinTournamentModalProps> = ({
    open,
    onClose,
    winnerInformation,
    rankingData = [],
    secondsToClose = 15,
    closeLastTableUponWinSeconds,
    formatCurrency,
    container,
    currentUsername
}) => {
    const countdownSeconds = closeLastTableUponWinSeconds || secondsToClose;
    const [remaining, setRemaining] = useState(countdownSeconds);

    useEffect(() => {
        if (!open) return;
        setRemaining(countdownSeconds);
    }, [open, countdownSeconds]);

    useEffect(() => {
        if (!open) return;
        const interval = setInterval(() => {
            setRemaining(prev => {
                if (prev <= 1) {
                    clearInterval(interval);
                    onClose();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [open, onClose]);

    const sortedRankings = useMemo(() => {
        return [...(rankingData || [])].sort((a, b) => a.position - b.position);
    }, [rankingData]);

    return (
        <StyledModal
            open={open}
            onClose={onClose}
            closeAfterTransition
            disableAutoFocus
            disableEnforceFocus
            BackdropProps={{
                timeout: 300,
                onClick: onClose,
                sx: { backgroundColor: 'transparent' }
            }}
            slotProps={{
                backdrop: { style: { backgroundColor: 'transparent' } }
            }}
            container={container}
        >
            <Fade in={open} timeout={300}>
                <ContentBox style={!isMobile ? { marginBottom: '60px' } : {}}>
                    <HeaderRow>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" style={{ width: isMobile ? '25%' : '33%' }}>
                            <Stack>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <WinnerTitle>{t('WinTournamentModal:mttWinner')}</WinnerTitle>
                                </Stack>
                            </Stack>
                        </Stack>
                        <WinnerName style={{ width: isMobile ? '50%' : '33%' }}>
                            <Stack direction='column' alignItems='center' position='relative'>
                                <Box style={{ position: 'relative' }}>
                                    <Avatar
                                        style={{
                                            position: 'relative',
                                            borderRadius: '50%',
                                            border: '5px solid #1C1C23',
                                            outline: '2px solid #CC976D',
                                            zIndex: 2,
                                            marginBottom: '10px',
                                            width: '62px',
                                            height: '62px',
                                        }}
                                        alt={`${winnerInformation?.username || 'Winner'} avatar`}
                                    >
                                        {winnerInformation?.username?.charAt(0) || 'W'}
                                    </Avatar>
                                </Box>
                                {winnerInformation?.country && (
                                    <ReactCountryFlag
                                        countryCode={winnerInformation.country}
                                        svg
                                        style={{ width: '29px', height: '18px', flexShrink: 0, borderRadius: '4px', position: 'absolute', top: '52px', left: 'calc(50% - 52px)', zIndex: 2, transform: 'translateX(-50%)' }}
                                    />
                                )}
                                <Stack style={{ textAlign: 'center', justifyContent: 'center' }}>
                                    <Typography style={{ fontSize: isMobile ? '16px' : '18px', lineHeight: '18px', fontWeight: 600, color: '#ffffff', fontFamily: '"Inter", serif' }}>{winnerInformation?.username || 'Winner'}</Typography>
                                </Stack>
                                <img src={winnerGraphic} alt="winner graphic" style={{ position: 'absolute', zIndex: 1, bottom: '-0.75rem', left: '50%', transform: 'translateX(-50%)' }} />
                            </Stack>
                        </WinnerName>
                        <Stack style={{ width: isMobile ? '25%' : '33%', flex: 1, height: '100%', minHeight: 0, justifyContent: 'flex-end', alignItems: 'flex-end', alignSelf: 'end' }}>
                            <img src={winnerGraphicAlt} alt="winner graphic" style={{ marginBottom: isMobile ? '0.5rem' : '0.5rem' }} />
                            <Typography 
                                style={{ 
                                    display: 'inline-block',
                                    fontSize: '12px', 
                                    lineHeight: isMobile ? '18px' : '1.5',
                                    fontWeight: 600, 
                                    color: '#FFFFFF', 
                                    fontFamily: '"Inter", serif', 
                                    textAlign: isMobile ? 'center' : 'right', 
                                    backgroundColor: '#3E414D', 
                                    borderRadius: isMobile ? '6px' : '30px', 
                                    padding: isMobile ? '0 0.5rem' : '0 1rem' 
                                }}
                            >
                                {t('WinTournamentModal:prize')}
                                <span style={{ fontSize: '16px', color: '#3AD65D', marginLeft: isMobile ? '0' : '0.5rem' }}>
                                    {isMobile && (<br />)}
                                    { typeof winnerInformation?.prize === 'number' 
                                        ? `$${formatCurrency(winnerInformation.prize)}` 
                                        : typeof winnerInformation?.chips === 'number' 
                                            ? winnerInformation.chips.toLocaleString() 
                                            : 'N/A' }
                                </span>
                            </Typography>
                        </Stack>
                    </HeaderRow>

                    <Box sx={{ p: 2 }}>
                        <Typography sx={{ color: '#9eb0c5', fontFamily: '"Inter", serif', mb: 1 }}>
                            {t('WinTournamentModal:tournamentWillClose')} <strong style={{ color: '#fff' }}>{remaining}s</strong>
                        </Typography>

                        <RankingsTable>
                            <RankingsRow sx={{ color: '#9eb0c5', fontWeight: 600 }}>
                                <Box>{t('WinTournamentModal:headers.rank')}</Box>
                                <Box>{t('WinTournamentModal:headers.player')}</Box>
                                {!isMobile && (
                                    <Box sx={{ textAlign: 'right' }}>{t('WinTournamentModal:headers.chips')}</Box>
                                )}
                                <Box sx={{ textAlign: 'right' }}>{t('WinTournamentModal:headers.prize')}</Box>
                            </RankingsRow>
                            {sortedRankings.map((r, index) => {
                                const isCurrentUser = currentUsername && r.username === currentUsername;
                                return (
                                    <RankingsRow key={r.id} sx={{ 
                                        backgroundColor: index % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
                                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' }
                                    }}>
                                        <Box sx={{ 
                                            color: isCurrentUser ? '#F93751' : (r.position === 1 ? '#FFC496' : '#9eb0c5'),
                                            fontWeight: r.position === 1 ? 600 : 400
                                        }}>
                                            {r.position}
                                        </Box>
                                        <Box sx={{ 
                                            color: isCurrentUser ? '#F93751' : (r.position === 1 ? '#FFC496' : '#fff'), 
                                            fontWeight: r.position === 1 ? 700 : 500,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            overflow: 'hidden',
                                        }}>
                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {r.username}
                                                {isCurrentUser ? <span style={{ color: '#F93751' }}> {t('WinTournamentModal:you')}</span> : null}
                                            </span>
                                            {r.country && (
                                                <ReactCountryFlag
                                                    countryCode={r.country}
                                                    svg
                                                    style={{ width: '16px', height: '14px', flexShrink: 0, borderRadius: '4px' }}
                                                />
                                            )}
                                        </Box>
                                        {!isMobile && (
                                            <Box sx={{ 
                                                textAlign: 'right', 
                                                color: isCurrentUser ? '#F93751' : (r.position === 1 ? '#FFC496' : '#9eb0c5'),
                                                fontWeight: r.balance > 0 ? 600 : 400
                                            }}>
                                                {r.balance.toLocaleString()}
                                            </Box>
                                        )}
                                        <Box sx={{ 
                                            textAlign: 'right', 
                                            color: isCurrentUser ? '#F93751' : (r.position === 1 ? '#FFC496' : r.prize > 0 ? '#FFFFFF' : '#9EB0C5'),
                                            fontWeight: r.prize > 0 ? 600 : 400
                                        }}>
                                            {r.prize > 0 ? `$${formatCurrency(r.prize)}` : '—'}
                                        </Box>
                                    </RankingsRow>
                                );
                            })}
                        </RankingsTable>
                    </Box>
                </ContentBox>
            </Fade>
        </StyledModal>
    );
};

export default React.memo(WinTournamentModal);


